import React, { useState, useEffect, useCallback, useMemo } from 'react';
// === LEADS-ANALYTICS v1 ===
import { adminAnalyticsAPI, myAnalyticsAPI } from '../lib/api';
import { MyActionPlanCard } from '../components/MyActionPlanCard';
import LeadFeesShareDialog from '../components/LeadFeesShareDialog';
import {
  ResponsiveContainer as RC2, BarChart as BC2, Bar as Bar2,
  XAxis as XA2, YAxis as YA2, CartesianGrid as CG2,
  Tooltip as RT2, Legend as L2,
} from 'recharts';
import {
  Card as AnaCard, CardContent as AnaCardContent,
  CardHeader as AnaCardHeader, CardTitle as AnaCardTitle,
} from '../components/ui/card';
import { Badge as AnaBadge } from '../components/ui/badge';
import {
  Table as AnaTable, TableBody as AnaTableBody,
  TableCell as AnaTableCell, TableHead as AnaTableHead,
  TableHeader as AnaTableHeader, TableRow as AnaTableRow,
} from '../components/ui/table';
import { BarChart3 as AnaBarChart3, TrendingUp as AnaTrendingUp, Users as AnaUsers, Target as AnaTarget, Clock as AnaClock, ChevronDown as AnaChevronDown, ChevronUp as AnaChevronUp , MessageCircle } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { leadsAPI, leadDashboardsAPI, incentiveAPI, admissionsAPI } from '../lib/api';import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Plus, Search, Edit, Trash2, Download, MessageSquarePlus, Phone, Calendar,
  CheckCircle2, Clock, IndianRupee, Target, TrendingUp, Flame, Activity,
  RefreshCw, Users, MapPin, GraduationCap, Loader2, Award, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Confirmed', 'Cancelled'];
const SCHOLARSHIP_OPTIONS = ['Received', 'Not Received'];

const statusBadge = (status) => {
  const map = {
    Pending: 'bg-slate-100 text-slate-700',
    'In Progress': 'bg-amber-100 text-amber-700',
    Confirmed: 'bg-emerald-100 text-emerald-700',
    Cancelled: 'bg-rose-100 text-rose-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const MultiSelect = ({ options, selected, onChange, placeholder, getKey = (o) => o.id, getLabel = (o) => o.name, getSub = () => '', disabled }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const low = q.toLowerCase();
    return options.filter((o) => getLabel(o).toLowerCase().includes(low) || (getSub(o) || '').toLowerCase().includes(low));
  }, [options, q, getLabel, getSub]);
  const toggle = (key) => {
    const exists = selected.includes(key);
    onChange(exists ? selected.filter((s) => s !== key) : [...selected, key]);
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        className="w-full border rounded-md px-3 py-2 text-left text-sm bg-white hover:border-slate-400 flex items-center justify-between"
        disabled={disabled}
        data-testid="multiselect-toggle"
      >
        <span className="truncate text-slate-700">{selected.length ? `${selected.length} selected` : (placeholder || 'Select...')}</span>
        <span className="text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border rounded-md shadow-lg overflow-hidden">
          {/* Sticky top bar with Clear + Done */}
          <div className="flex items-center justify-between border-b p-2 bg-slate-50">
            <span className="text-xs text-slate-600 font-medium" data-testid="multiselect-count">
              {selected.length} selected
            </span>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); onChange([]); }}
                className="h-7 text-xs"
                data-testid="multiselect-clear"
              >
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="h-7 text-xs bg-[#0066CC] hover:bg-[#0052A3]"
                data-testid="multiselect-done"
              >
                Done
              </Button>
            </div>
          </div>
          <div className="p-2 border-b">
            <Input autoFocus placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="text-xs text-slate-400 p-4 text-center">No options</div>
            ) : filtered.map((o) => {
              const key = getKey(o);
              const on = selected.includes(key);
              return (
                <div
                  key={key}
                  onClick={() => toggle(key)}
                  className={`px-3 py-2 cursor-pointer text-sm flex items-center gap-2 hover:bg-slate-50 ${on ? 'bg-blue-50' : ''}`}
                >
                  <input type="checkbox" readOnly checked={on} />
                  <div className="flex-1">
                    <div className="font-medium">{getLabel(o)}</div>
                    {getSub(o) && <div className="text-xs text-slate-500">{getSub(o)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const IncentiveCard = ({ data }) => {
  if (!data) return null;
  const pct = Math.min(data.target_progress_pct || 0, 100);
  return (
    <Card className="border-0 shadow-sm overflow-hidden" data-testid="incentive-tracker-card">
      <div className="bg-gradient-to-br from-[#0066CC] to-[#003d7a] text-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide opacity-80">Your incentive this month</p>
            <p className="text-3xl font-semibold mt-1">{inr(data.total_incentive)}</p>
          </div>
          <Award className="h-10 w-10 opacity-80" />
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs opacity-90 mb-1">
            <span>{data.confirmed_count}/{data.monthly_target} admissions</span>
            <span>{pct.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
            <div className="bg-white h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
      <CardContent className="p-4 grid grid-cols-3 gap-3 text-xs">
        <div>
          <p className="text-slate-500">Base total</p>
          <p className="font-semibold text-slate-900 mt-0.5">{inr(data.base_total)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{inr(data.base_incentive)}/adm × {data.confirmed_count}</p>
        </div>
        <div>
          <p className="text-slate-500">Target bonus</p>
          <p className={`font-semibold mt-0.5 ${data.target_hit ? 'text-emerald-600' : 'text-slate-400'}`}>
            {data.target_hit ? inr(data.complementary_earned) : 'Locked'}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">Unlocks at {data.monthly_target}</p>
        </div>
        <div>
          <p className="text-slate-500">Extra adm</p>
          <p className="font-semibold text-slate-900 mt-0.5">{inr(data.extra_total)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{data.extra_admissions} × {inr(data.additional_incentive)}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const ActivityTimeline = ({ lead, onAddRemark }) => {
  const [text, setText] = useState('');
  const events = [...(lead.activity || []), ...(lead.remarks || []).map((r) => ({
    id: r.id, type: 'remark', message: r.text, at: r.created_at, actor_name: r.created_by_name,
  }))].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
  const submit = async () => {
    if (!text.trim()) return;
    await onAddRemark(text);
    setText('');
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a remark or note..."
          rows={2}
          className="resize-none"
          data-testid="remark-input"
        />
        <Button onClick={submit} disabled={!text.trim()} data-testid="add-remark-btn" className="self-start">
          <MessageSquarePlus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-2 max-h-80 overflow-y-auto border rounded-md">
        {events.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-6">No activity yet</div>
        ) : events.map((e) => (
          <div key={e.id} className="flex items-start gap-2 p-3 border-b last:border-b-0">
            <div className="mt-1">
              {e.type === 'remark' ? (
                <MessageSquarePlus className="h-4 w-4 text-blue-500" />
              ) : e.type === 'status' ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              ) : e.type?.startsWith('visit') ? (
                <Calendar className="h-4 w-4 text-amber-500" />
              ) : e.type === 'reassigned' ? (
                <Users className="h-4 w-4 text-purple-500" />
              ) : (
                <Activity className="h-4 w-4 text-slate-400" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-800">{e.message}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {e.actor_name || 'System'} · {e.at ? new Date(e.at).toLocaleString() : ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Leads() {
  const { user, isAdmin } = useAuth();
  const [waFeesLead, setWaFeesLead] = useState(null);
  const designation = user?.designation;
  const canManageAll = isAdmin() || designation === 'Admission Manager';
  const isTeamLead = designation === 'Team Lead';
  // === LEADS-ANALYTICS v1 ===
  // === LEADS-FILTER-WIRE v1 ===
  const showAnalytics = !!user && (user.role === 'admin' || isTeamLead);
  const [leadsAnalytics, setLeadsAnalytics] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(true); // open by default
  const [loadingLeadsAnalytics, setLoadingLeadsAnalytics] = useState(false);
  // === MY-PERF-LEADS v1 ===
  const [myAnalytics, setMyAnalytics] = useState(null);
  const isCounselorView = !!user && user.role !== 'admin' && !isTeamLead;
  useEffect(() => {
    if (!isCounselorView) return;
    myAnalyticsAPI.leads({ months: 6 })
      .then((r) => setMyAnalytics(r.data))
      .catch(() => { /* silent */ });
  }, [isCounselorView]);

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [roleInfo, setRoleInfo] = useState(null);

  const [query, setQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCounselor, setFilterCounselor] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all'); // YYYY-MM or 'all'
  // === LEADS-ANALYTICS-EFFECT moved v2 ===
  useEffect(() => {
    if (!showAnalytics || !analyticsOpen) return;
    setLoadingLeadsAnalytics(true);
    const params = { months: 6 };
    if (filterStatus !== 'all')   params.admission_status = filterStatus;
    if (filterCounselor !== 'all') params.counselor_id    = filterCounselor;
    if (filterMonth !== 'all') {
      const [yy, mm] = filterMonth.split('-');
      params.year = yy; params.month = mm;
    }
    adminAnalyticsAPI.leads(params)
      .then((r) => setLeadsAnalytics(r.data))
      .catch(() => { /* silent */ })
      .finally(() => setLoadingLeadsAnalytics(false));
  }, [showAnalytics, analyticsOpen, filterStatus, filterCounselor, filterMonth]);

  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);

  // Confirm → Admission dialog
  const [admissionDialogOpen, setAdmissionDialogOpen] = useState(false);
  const [admissionForm, setAdmissionForm] = useState(null);
  const [savingAdmission, setSavingAdmission] = useState(false);
  const [pendingConfirmLead, setPendingConfirmLead] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [saving, setSaving] = useState(false);

  const [colleges, setColleges] = useState([]);
  const [courses, setCourses] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [incentive, setIncentive] = useState(null);
  const [stats, setStats] = useState(null);
  // === LEADS-FILTER-WIRE v1 moved ===
  // For admin / team lead, override the top-row stats with values derived from
  // the filter-aware /admin/analytics/leads response so the tiles change when
  // status / counselor / month filters are toggled.
  const viewStats = (() => {
    if (showAnalytics && leadsAnalytics) {
      const k = leadsAnalytics.kpis || {};
      const byStage = Object.fromEntries((leadsAnalytics.funnel || []).map((r) => [r.stage, r.count]));
      return {
        ...(stats || {}),
        total_leads:          k.total_leads ?? 0,
        admissions_confirmed: k.confirmed_total ?? 0,
        potential_admissions: byStage['In Progress'] ?? 0,
        current_incentive:    stats?.current_incentive || 0,
        potential_incentive:  stats?.potential_incentive || 0,
      };
    }
    return stats;
  })();

  const [form, setForm] = useState({
    name: '', phone: '', alt_phone: '', place: '',
    suggested_college_ids: [], course_ids: [],
    visiting_date: '', college_visiting_date: '',
    scholarship_status: 'Not Received', admission_status: 'Pending',
    counselor_id: '', remarks_text: '',
  });

  const resetForm = () => {
    setForm({
      name: '', phone: '', alt_phone: '', place: '',
      suggested_college_ids: [], course_ids: [],
      visiting_date: '', college_visiting_date: '',
      scholarship_status: 'Not Received', admission_status: 'Pending',
      counselor_id: '', remarks_text: '',
    });
    setEditLead(null);
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort };
      if (query) params.q = query;
      if (filterStatus !== 'all') params.admission_status = filterStatus;
      if (filterCounselor !== 'all') params.counselor_id = filterCounselor;
      if (filterMonth !== 'all') {
        const [y, m] = filterMonth.split('-');
        params.year = y; params.month = m;
      }
      const res = await leadsAPI.list(params);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
      setRoleInfo(res.data.role_info || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [page, sort, query, filterStatus, filterCounselor, filterMonth]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    (async () => {
      try {
        const res = await leadsAPI.featuredColleges();
        setColleges(res.data.colleges || []);
      } catch (e) { /* silent */ }
      try {
        if (!isAdmin()) {
          const inc = await incentiveAPI.mine();
          setIncentive(inc.data);
          const dash = await leadDashboardsAPI.counselor();
          setStats({
            total_leads: dash.data.totals.total_leads,
            admissions_confirmed: dash.data.totals.admissions_confirmed,
            potential_admissions: dash.data.totals.potential_admissions,
            current_incentive: dash.data.incentive?.total_incentive || 0,
            potential_incentive: dash.data.potential_incentive?.total_incentive || 0,
          });
        } else {
          const dash = await leadDashboardsAPI.admin();
          setStats({
            total_leads: dash.data.totals.total_leads,
            admissions_confirmed: dash.data.totals.admissions_confirmed,
            potential_admissions: dash.data.totals.potential_admissions,
            current_incentive: dash.data.totals.incentive_payout || 0,
            potential_incentive: dash.data.totals.potential_incentive_payout || 0,
          });
        }
      } catch (e) { /* silent */ }
      if (canManageAll || isTeamLead) {
        try {
          const { data } = await leadDashboardsAPI.teamLead();
          setCounselors((data.rows || []).map((r) => ({ id: r.counselor_id, name: r.name, designation: r.designation })));
        } catch (e) { /* silent */ }
      }
    })();
  }, [canManageAll, isTeamLead, isAdmin]);

  // When suggested colleges change, auto-fetch their courses
  useEffect(() => {
    if (!form.suggested_college_ids.length) { setCourses([]); return; }
    (async () => {
      try {
        const res = await leadsAPI.collegeCourses(form.suggested_college_ids);
        setCourses(res.data.courses || []);
      } catch (e) { setCourses([]); }
    })();
  }, [form.suggested_college_ids]);

  const refreshStats = async () => {
    try {
      if (!isAdmin()) {
        const dash = await leadDashboardsAPI.counselor();
        const inc = await incentiveAPI.mine();
        setIncentive(inc.data);
        setStats({
          total_leads: dash.data.totals.total_leads,
          admissions_confirmed: dash.data.totals.admissions_confirmed,
          potential_admissions: dash.data.totals.potential_admissions,
          current_incentive: dash.data.incentive?.total_incentive || 0,
          potential_incentive: dash.data.potential_incentive?.total_incentive || 0,
        });
      } else {
        const dash = await leadDashboardsAPI.admin();
        setStats({
          total_leads: dash.data.totals.total_leads,
          admissions_confirmed: dash.data.totals.admissions_confirmed,
          potential_admissions: dash.data.totals.potential_admissions,
          current_incentive: dash.data.totals.incentive_payout || 0,
          potential_incentive: dash.data.totals.potential_incentive_payout || 0,
        });
      }
    } catch (e) { /* silent */ }
  };

  // Load courses for the admission dialog when college changes
  useEffect(() => {
    if (!admissionDialogOpen || !admissionForm?.college_id) return;
    (async () => {
      try {
        const res = await leadsAPI.collegeCourses([admissionForm.college_id]);
        setCourses((prev) => {
          const map = new Map(prev.map((c) => [c.id, c]));
          (res.data.courses || []).forEach((c) => map.set(c.id, c));
          return Array.from(map.values());
        });
      } catch (e) { /* silent */ }
    })();
  }, [admissionDialogOpen, admissionForm?.college_id]);

  const openCreate = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (lead) => {
    setEditLead(lead);
    setForm({
      name: lead.name || '', phone: lead.phone || '', alt_phone: lead.alt_phone || '', place: lead.place || '',
      suggested_college_ids: lead.suggested_college_ids || [],
      course_ids: lead.course_ids || [],
      course_names: lead.course_names || [],
      visiting_date: lead.visiting_date || '',
      college_visiting_date: lead.college_visiting_date || '',
      scholarship_status: lead.scholarship_status || 'Not Received',
      admission_status: lead.admission_status || 'Pending',
      counselor_id: lead.counselor_id || '',
      remarks_text: '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    // For counselor users — if they changed the status to Confirmed via the Edit form,
    // route through the Admission dialog (same flow as the inline table dropdown).
    const isCounselorUser = !isAdmin() && !canManageAll && !isTeamLead;
    if (editLead && isCounselorUser && form.admission_status === 'Confirmed' && editLead.admission_status !== 'Confirmed') {
      // First save the other fields (but keep the previous status), then open the admission dialog
      const prevStatus = editLead.admission_status || 'Pending';
      try {
        const selectedColleges = colleges.filter((c) => form.suggested_college_ids.includes(c.id));
        const selectedCourses = courses.filter((c) => form.course_ids.includes(c.id));
        const payload = {
          name: form.name.trim(),
          phone: form.phone.trim(),
          alt_phone: (form.alt_phone || '').trim(),
          place: form.place?.trim() || '',
          suggested_college_ids: form.suggested_college_ids,
          suggested_college_names: selectedColleges.map((c) => c.name),
          course_ids: form.course_ids,
          course_names: selectedCourses.length ? selectedCourses.map((c) => c.name) : (form.course_names || []),
          visiting_date: form.visiting_date || null,
          college_visiting_date: form.college_visiting_date || null,
          scholarship_status: form.scholarship_status,
          admission_status: prevStatus, // keep old status for now
        };
        const { data } = await leadsAPI.update(editLead.id, payload);
        // Now open the admission dialog with fresh lead data
        setDialogOpen(false);
        resetForm();
        openAdmissionDialog(data.lead);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Save failed');
      }
      return;
    }

    setSaving(true);
    try {
      const selectedColleges = colleges.filter((c) => form.suggested_college_ids.includes(c.id));
      const selectedCourses = courses.filter((c) => form.course_ids.includes(c.id));
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        alt_phone: (form.alt_phone || '').trim(),
        place: form.place?.trim() || '',
        suggested_college_ids: form.suggested_college_ids,
        suggested_college_names: selectedColleges.map((c) => c.name),
        course_ids: form.course_ids,
        course_names: selectedCourses.length ? selectedCourses.map((c) => c.name) : (form.course_names || []),
        visiting_date: form.visiting_date || null,
        college_visiting_date: form.college_visiting_date || null,
        scholarship_status: form.scholarship_status,
        admission_status: form.admission_status,
      };
      if (canManageAll || isTeamLead) payload.counselor_id = form.counselor_id || undefined;
      if (!editLead && form.remarks_text?.trim()) payload.remarks_text = form.remarks_text.trim();

      if (editLead) {
        await leadsAPI.update(editLead.id, payload);
        toast.success('Lead updated');
      } else {
        await leadsAPI.create(payload);
        toast.success('Lead created');
      }
      setDialogOpen(false);
      resetForm();
      fetchLeads();
      refreshStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const monthOptions = useMemo(() => {
    const opts = [];
    const d = new Date();
    for (let i = 0; i < 12; i += 1) {
      const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
      const value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const label = dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      opts.push({ value, label });
    }
    return opts;
  }, []);

  const quickStatus = async (lead, status) => {
    // Counselor selecting "Confirmed" → open Admission form pre-filled
    const isCounselorUser = !isAdmin() && !canManageAll && !isTeamLead;
    if (status === 'Confirmed' && lead.admission_status !== 'Confirmed' && isCounselorUser) {
      openAdmissionDialog(lead);
      return;
    }
    try {
      const { data } = await leadsAPI.update(lead.id, { admission_status: status });
      toast.success(`Status → ${status}`);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? data.lead : l)));
      refreshStats();
      if (status === 'Confirmed' && !isAdmin()) {
        toast.success(`+${inr(incentive?.base_incentive || 500)} earned!`, { duration: 4000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const openAdmissionDialog = (lead) => {
    // Pick primary college+course from lead
    const collegeId = (lead.suggested_college_ids || [])[0] || '';
    const collegeName = (lead.suggested_college_names || [])[0] || '';
    const courseId = (lead.course_ids || [])[0] || '';
    const courseName = (lead.course_names || [])[0] || '';
    setPendingConfirmLead(lead);
    setAdmissionForm({
      candidate_name: lead.name || '',
      place: lead.place || '',
      college_id: collegeId,
      college_name: collegeName,
      course_id: courseId,
      course_name: courseName,
      admission_date: new Date().toISOString().slice(0, 10),
      total_fees: '',
      fees_paid: '',
      scholarship_amount: '',
      remark: (lead.remarks && lead.remarks.length ? lead.remarks[lead.remarks.length - 1].text : ''),
    });
    setAdmissionDialogOpen(true);
  };

  const saveAdmission = async () => {
    if (!admissionForm) return;
    if (!admissionForm.college_id || !admissionForm.course_id) {
      toast.error('Please select College and Course');
      return;
    }
    if (!admissionForm.total_fees) {
      toast.error('Total fees is required');
      return;
    }
    setSavingAdmission(true);
    try {
      await admissionsAPI.create({
        candidate_name: admissionForm.candidate_name,
        place: admissionForm.place,
        college_id: admissionForm.college_id,
        course_id: admissionForm.course_id,
        admission_date: admissionForm.admission_date,
        fees_paid: Number(admissionForm.fees_paid || 0),
        total_fees: Number(admissionForm.total_fees || 0),
        instalments: [],
        remark: admissionForm.remark || '',
        scholarship_amount: admissionForm.scholarship_amount ? Number(admissionForm.scholarship_amount) : null,
      });
      // Now mark lead confirmed AND lock the lead down to the single college/course
      // that was actually chosen in the Add Admission dialog.
      if (pendingConfirmLead) {
        const { data } = await leadsAPI.update(pendingConfirmLead.id, {
          admission_status: 'Confirmed',
          suggested_college_ids: admissionForm.college_id ? [admissionForm.college_id] : [],
          suggested_college_names: admissionForm.college_name ? [admissionForm.college_name] : [],
          course_ids: admissionForm.course_id ? [admissionForm.course_id] : [],
          course_names: admissionForm.course_name ? [admissionForm.course_name] : [],
        });
        setLeads((ls) => ls.map((l) => (l.id === pendingConfirmLead.id ? data.lead : l)));
      }
      toast.success('Admission saved & lead marked Confirmed');
      setAdmissionDialogOpen(false);
      setPendingConfirmLead(null);
      setAdmissionForm(null);
      refreshStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Admission save failed');
    } finally {
      setSavingAdmission(false);
    }
  };

  const admissionCourseOptions = useMemo(() => {
    if (!admissionForm?.college_id) return [];
    // When the dialog is opened from a Confirmed-status change, restrict to only the
    // courses the lead originally suggested (and that belong to the chosen college).
    const leadCourseIds = pendingConfirmLead?.course_ids || [];
    const matchesCollege = (c) => !c.college_id || String(c.college_id) === String(admissionForm.college_id);
    if (leadCourseIds.length) {
      return courses.filter((c) => leadCourseIds.includes(c.id) && matchesCollege(c));
    }
    return courses.filter(matchesCollege);
  }, [admissionForm?.college_id, courses, pendingConfirmLead?.course_ids]);

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete lead ${lead.name}?`)) return;
    try {
      await leadsAPI.remove(lead.id);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const addRemark = async (text) => {
    if (!editLead) return;
    try {
      const { data } = await leadsAPI.addRemark(editLead.id, text);
      setEditLead(data.lead);
      toast.success('Remark added');
    } catch (e) { toast.error('Add failed'); }
  };

  const doExport = async () => {
    try {
      const res = await leadsAPI.exportCSV();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      toast.success('Export downloaded');
    } catch (e) { toast.error('Export failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <Flame className="h-6 w-6 text-[#0066CC]" /> Lead Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {roleInfo?.is_admin ? 'Admin view · all leads'
                : roleInfo?.is_manager ? 'Manager view · all leads'
                : roleInfo?.is_team_lead ? 'Team Lead view · your team leads'
                : 'Your leads'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={doExport} data-testid="export-leads-btn">
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
            <Button variant="outline" onClick={fetchLeads} data-testid="refresh-leads-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button onClick={openCreate} data-testid="add-lead-btn" className="bg-[#0066CC] hover:bg-[#0052A3]">
              <Plus className="h-4 w-4 mr-1" /> Add Lead
            </Button>
          </div>
        </div>

        
      {/* === MY-PERF-LEADS v1 === Personal analytics for counselors */}
      {isCounselorView && myAnalytics && (
        <AnaCard className="border-0 shadow-sm" data-testid="my-perf-card">
          <AnaCardHeader className="pb-3">
            <AnaCardTitle className="text-base flex items-center gap-2">
              <AnaTarget className="h-5 w-5 text-emerald-600" /> My Performance
              {myAnalytics.kpis.my_rank ? (
                <AnaBadge className="ml-auto bg-emerald-50 border-emerald-200 text-emerald-700">
                  Rank #{myAnalytics.kpis.my_rank} of {myAnalytics.kpis.total_in_cohort}
                </AnaBadge>
              ) : (
                <AnaBadge variant="outline" className="ml-auto">Last 6 months</AnaBadge>
              )}
            </AnaCardTitle>
          </AnaCardHeader>
          <AnaCardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border border-slate-200 bg-blue-50 px-3 py-2">
                <p className="text-[11px] uppercase text-slate-500 font-semibold">My Leads</p>
                <p className="text-2xl font-bold text-slate-900">{myAnalytics.kpis.total_leads}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase text-slate-500 font-semibold">Confirmed</p>
                <p className="text-2xl font-bold text-emerald-700">{myAnalytics.kpis.confirmed_total}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-purple-50 px-3 py-2">
                <p className="text-[11px] uppercase text-slate-500 font-semibold">My Conversion</p>
                <p className="text-2xl font-bold text-purple-700">{myAnalytics.kpis.conversion_pct}%</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-amber-50 px-3 py-2">
                <p className="text-[11px] uppercase text-slate-500 font-semibold">Avg time to admit</p>
                <p className="text-2xl font-bold text-amber-700 flex items-baseline gap-1">{myAnalytics.kpis.avg_days_to_conversion}<span className="text-xs font-normal text-slate-500">days</span></p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="my-funnel-chart">
                <p className="text-xs font-semibold text-slate-600 mb-2 px-1">My Conversion Funnel</p>
                <RC2 width="100%" height={240}>
                  <BC2 data={myAnalytics.funnel} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                    <CG2 strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XA2 dataKey="stage" tick={{ fontSize: 11 }} />
                    <YA2 tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RT2 />
                    <Bar2 dataKey="count" fill="#10b981" name="Leads" radius={[4, 4, 0, 0]} />
                  </BC2>
                </RC2>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="my-trend-chart">
                <p className="text-xs font-semibold text-slate-600 mb-2 px-1">My Monthly Trend</p>
                <RC2 width="100%" height={240}>
                  <BC2 data={myAnalytics.monthly_trend} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                    <CG2 strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XA2 dataKey="month" tick={{ fontSize: 11 }} />
                    <YA2 tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RT2 />
                    <L2 wrapperStyle={{ fontSize: 11 }} />
                    <Bar2 dataKey="new_leads" fill="#3b82f6" name="New Leads" />
                    <Bar2 dataKey="confirmed" fill="#10b981" name="Confirmed" />
                  </BC2>
                </RC2>
              </div>
            </div>
          </AnaCardContent>
        </AnaCard>
      )}

      {/* === ACTION-PLAN v1 === */}
      {isCounselorView && <MyActionPlanCard testIdSuffix="-leads" />}

      {/* === LEADS-ANALYTICS v1 === Advanced Analytics (admin / team lead) */}
      {showAnalytics && (
        <AnaCard className="border-0 shadow-sm" data-testid="leads-analytics-card">
          <AnaCardHeader className="pb-3 cursor-pointer" onClick={() => setAnalyticsOpen((v) => !v)}>
            <AnaCardTitle className="text-base flex items-center gap-2">
              <AnaBarChart3 className="h-5 w-5 text-indigo-600" /> Advanced Analytics
              <AnaBadge variant="outline" className="ml-auto bg-indigo-50 border-indigo-200 text-indigo-700">
                Last 6 months
              </AnaBadge>
              {analyticsOpen ? <AnaChevronUp className="h-4 w-4 text-slate-400" /> : <AnaChevronDown className="h-4 w-4 text-slate-400" />}
            </AnaCardTitle>
          </AnaCardHeader>
          {analyticsOpen && (
            <AnaCardContent className="space-y-4">
              {loadingLeadsAnalytics && <p className="text-sm text-slate-500">Loading analytics…</p>}
              {leadsAnalytics && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-lg border border-slate-200 bg-blue-50 px-3 py-2">
                      <p className="text-[11px] uppercase text-slate-500 font-semibold">Total Leads</p>
                      <p className="text-2xl font-bold text-slate-900">{leadsAnalytics.kpis.total_leads}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-emerald-50 px-3 py-2">
                      <p className="text-[11px] uppercase text-slate-500 font-semibold">Confirmed</p>
                      <p className="text-2xl font-bold text-emerald-700">{leadsAnalytics.kpis.confirmed_total}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-purple-50 px-3 py-2">
                      <p className="text-[11px] uppercase text-slate-500 font-semibold">Conversion</p>
                      <p className="text-2xl font-bold text-purple-700">{leadsAnalytics.kpis.overall_conversion_pct}%</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-amber-50 px-3 py-2">
                      <p className="text-[11px] uppercase text-slate-500 font-semibold">Avg time to admit</p>
                      <p className="text-2xl font-bold text-amber-700 flex items-baseline gap-1">{leadsAnalytics.kpis.avg_days_to_conversion}<span className="text-xs font-normal text-slate-500">days</span></p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="leads-funnel-chart">
                      <p className="text-xs font-semibold text-slate-600 mb-2 px-1">Conversion Funnel</p>
                      <RC2 width="100%" height={260}>
                        <BC2 data={leadsAnalytics.funnel} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                          <CG2 strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XA2 dataKey="stage" tick={{ fontSize: 11 }} />
                          <YA2 tick={{ fontSize: 11 }} allowDecimals={false} />
                          <RT2 />
                          <Bar2 dataKey="count" fill="#6366f1" name="Leads" radius={[4, 4, 0, 0]} />
                        </BC2>
                      </RC2>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="leads-trend-chart">
                      <p className="text-xs font-semibold text-slate-600 mb-2 px-1">Monthly Trend</p>
                      <RC2 width="100%" height={260}>
                        <BC2 data={leadsAnalytics.monthly_trend} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                          <CG2 strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XA2 dataKey="month" tick={{ fontSize: 11 }} />
                          <YA2 tick={{ fontSize: 11 }} allowDecimals={false} />
                          <RT2 />
                          <L2 wrapperStyle={{ fontSize: 11 }} />
                          <Bar2 dataKey="new_leads" fill="#3b82f6" name="New Leads" />
                          <Bar2 dataKey="confirmed" fill="#10b981" name="Confirmed" />
                        </BC2>
                      </RC2>
                    </div>
                  </div>

                  {leadsAnalytics.per_counselor?.length > 0 && (
                    <div className="bg-white rounded-lg border border-slate-200" data-testid="leads-per-counselor">
                      <div className="px-3 py-2 border-b border-slate-100">
                        <p className="text-xs font-semibold text-slate-600">Per-Counselor Performance</p>
                      </div>
                      <div className="overflow-x-auto">
                        <AnaTable>
                          <AnaTableHeader>
                            <AnaTableRow>
                              <AnaTableHead>Counselor</AnaTableHead>
                              <AnaTableHead className="text-right">Leads</AnaTableHead>
                              <AnaTableHead className="text-right">In Progress</AnaTableHead>
                              <AnaTableHead className="text-right">Confirmed</AnaTableHead>
                              <AnaTableHead className="text-right">Conversion</AnaTableHead>
                            </AnaTableRow>
                          </AnaTableHeader>
                          <AnaTableBody>
                            {leadsAnalytics.per_counselor.map((r) => (
                              <AnaTableRow key={r.counselor_id}>
                                <AnaTableCell className="font-medium">{r.name}<span className="block text-xs text-slate-400">{r.designation}</span></AnaTableCell>
                                <AnaTableCell className="text-right">{r.leads}</AnaTableCell>
                                <AnaTableCell className="text-right text-amber-700">{r.in_progress}</AnaTableCell>
                                <AnaTableCell className="text-right font-semibold text-emerald-700">{r.confirmed}</AnaTableCell>
                                <AnaTableCell className="text-right">
                                  <AnaBadge className={r.conversion_pct >= 20 ? 'bg-emerald-100 text-emerald-700' : r.conversion_pct >= 10 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                                    {r.conversion_pct}%
                                  </AnaBadge>
                                </AnaTableCell>
                              </AnaTableRow>
                            ))}
                          </AnaTableBody>
                        </AnaTable>
                      </div>
                    </div>
                  )}
                </>
              )}
            </AnaCardContent>
          )}
        </AnaCard>
      )}

        {incentive && !isAdmin() && (<IncentiveCard data={incentive} />)}

        {viewStats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="leads-stats-row">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">Total Leads</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">{viewStats.total_leads}</p>
                  </div>
                  <Flame className="h-5 w-5 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">Admissions</p>
                    <p className="text-2xl font-semibold text-emerald-600 mt-1">{viewStats.admissions_confirmed}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Confirmed this month</p>
                  </div>
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">Potential Admissions</p>
                    <p className="text-2xl font-semibold text-amber-600 mt-1">{viewStats.potential_admissions}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">In Progress leads</p>
                  </div>
                  <TrendingUp className="h-5 w-5 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">Current Incentive</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">{inr(viewStats.current_incentive)}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">This month</p>
                  </div>
                  <IndianRupee className="h-5 w-5 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-purple-700">Potential Incentive</p>
                    <p className="text-2xl font-semibold text-purple-700 mt-1">{inr(viewStats.potential_incentive)}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">If all in-progress close</p>
                  </div>
                  <Target className="h-5 w-5 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search name or phone..."
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                  className="pl-9"
                  data-testid="leads-search-input"
                />
              </div>
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
                <SelectTrigger data-testid="leads-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
              {(canManageAll || isTeamLead) && (
                <Select value={filterCounselor} onValueChange={(v) => { setFilterCounselor(v); setPage(1); }}>
                  <SelectTrigger data-testid="leads-counselor-filter"><SelectValue placeholder="All counselors" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All counselors</SelectItem>
                    {counselors.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              <Select value={filterMonth} onValueChange={(v) => { setFilterMonth(v); setPage(1); }}>
                <SelectTrigger data-testid="leads-month-filter"><SelectValue placeholder="All months" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months</SelectItem>
                  {monthOptions.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="leads-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="visits">Upcoming visits</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm">{total} lead{total === 1 ? '' : 's'}</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center p-10 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Flame className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p>No leads yet. Click "Add Lead" to capture your first lead.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Interest</TableHead>
                    <TableHead>Visit</TableHead>
                    <TableHead>College Visited</TableHead>
                    <TableHead>Scholarship</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Latest Remark</TableHead>
                    {(canManageAll || isTeamLead) && <TableHead>Counselor</TableHead>}
                    <TableHead>Created</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="font-medium text-slate-900">{l.name}</div>
                        {l.place && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{l.place}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" />{l.phone}</div>
                        {l.alt_phone && (
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Phone className="h-3 w-3 text-slate-300" />
                            <span title="Alternate">{l.alt_phone}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-slate-600 max-w-[200px] truncate">
                          {(l.course_names || []).join(', ') || '—'}
                        </div>
                        {(l.suggested_college_names || []).length > 0 && (
                          <div className="text-[11px] text-slate-400 mt-0.5 truncate max-w-[200px]">
                            {l.suggested_college_names.slice(0, 2).join(' · ')}
                            {l.suggested_college_names.length > 2 ? ` +${l.suggested_college_names.length - 2}` : ''}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {l.college_visiting_date ? (
                          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1">
                            <CheckCircle2 className="h-3 w-3" /> {l.college_visiting_date}
                          </Badge>
                        ) : l.visiting_date ? (
                          <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-700 gap-1">
                            <Clock className="h-3 w-3" /> {l.visiting_date}
                          </Badge>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                      <TableCell>
                        {l.college_visiting_date ? (
                          <span className="text-xs text-slate-700">{l.college_visiting_date}</span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={l.scholarship_status === 'Received' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : ''}>
                          {l.scholarship_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select value={l.admission_status} onValueChange={(v) => quickStatus(l, v)}>
                          <SelectTrigger className={`h-8 text-xs w-32 ${statusBadge(l.admission_status)}`} data-testid={`status-select-${l.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const rs = l.remarks || [];
                          if (rs.length === 0) return <span className="text-xs text-slate-400">—</span>;
                          const last = rs[rs.length - 1];
                          const full = last.text || '';
                          const truncated = full.length > 60 ? full.slice(0, 60) + '…' : full;
                          return (
                            <div className="max-w-[220px]" title={`${full}\n— ${last.created_by_name || ''} · ${last.created_at ? new Date(last.created_at).toLocaleString() : ''}\n(${rs.length} total)`}>
                              <div className="text-xs text-slate-700 flex items-start gap-1">
                                <MessageSquare className="h-3 w-3 text-slate-400 shrink-0 mt-0.5" />
                                <span className="leading-snug">{truncated}</span>
                              </div>
                              <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                                {last.created_by_name || 'User'}
                                {rs.length > 1 && <span className="ml-1">· +{rs.length - 1} more</span>}
                              </p>
                            </div>
                          );
                        })()}
                      </TableCell>
                      {(canManageAll || isTeamLead) && (
                        <TableCell className="text-xs text-slate-600">{l.counselor_name || '—'}</TableCell>
                      )}
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        {l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        {l.updated_at ? new Date(l.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => setWaFeesLead(l)} data-testid={`send-fees-lead-${l.id}`} title="Send fees via WhatsApp" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50">
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(l)} data-testid={`edit-lead-${l.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeLead(l)} data-testid={`delete-lead-${l.id}`} className="text-rose-600 hover:text-rose-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination (basic) */}
        {total > 50 && (
          <div className="flex items-center justify-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1}>Prev</Button>
            <span className="text-sm text-slate-500">Page {page} of {Math.ceil(total / 50)}</span>
            <Button size="sm" variant="outline" onClick={() => setPage((p) => p + 1)} disabled={page >= Math.ceil(total / 50)}>Next</Button>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); resetForm(); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editLead ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editLead ? 'Edit Lead' : 'New Lead'}
            </DialogTitle>
            <DialogDescription>
              {editLead ? 'Update lead details, status, and timeline below.' : 'Capture new lead details. Phone number must be unique.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div>
              <Label>Name<span className="text-rose-500">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" />
            </div>
            <div>
              <Label>Phone<span className="text-rose-500">*</span></Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" />
            </div>
            <div>
              <Label>Alternate Contact Number</Label>
              <Input value={form.alt_phone} onChange={(e) => setForm({ ...form, alt_phone: e.target.value })} placeholder="Optional" data-testid="lead-alt-phone-input" />
            </div>
            <div>
              <Label>Place</Label>
              <Input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} data-testid="lead-place-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Suggested Colleges</Label>
              <MultiSelect
                options={colleges}
                selected={form.suggested_college_ids}
                onChange={(v) => setForm({ ...form, suggested_college_ids: v, course_ids: [] })}
                placeholder="Search and select featured colleges..."
                getSub={(c) => c.city}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Courses Interested</Label>
              <MultiSelect
                options={courses}
                selected={form.course_ids}
                onChange={(v) => setForm({ ...form, course_ids: v })}
                placeholder={form.suggested_college_ids.length ? 'Select courses...' : 'Select colleges first'}
                disabled={!form.suggested_college_ids.length}
              />
            </div>
            <div>
              <Label>Visit Planned</Label>
              <Input type="date" value={form.visiting_date || ''} onChange={(e) => setForm({ ...form, visiting_date: e.target.value })} data-testid="lead-visit-planned" />
            </div>
            <div>
              <Label>College Visited Date</Label>
              <Input type="date" value={form.college_visiting_date || ''} onChange={(e) => setForm({ ...form, college_visiting_date: e.target.value })} data-testid="lead-visit-actual" />
            </div>
            <div>
              <Label>Scholarship</Label>
              <Select value={form.scholarship_status} onValueChange={(v) => setForm({ ...form, scholarship_status: v })}>
                <SelectTrigger data-testid="lead-scholarship-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCHOLARSHIP_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {(canManageAll || isTeamLead) && (
              <div>
                <Label>Assign to Counselor</Label>
                <Select value={form.counselor_id || ''} onValueChange={(v) => setForm({ ...form, counselor_id: v })}>
                  <SelectTrigger data-testid="lead-counselor-select"><SelectValue placeholder="Select counselor" /></SelectTrigger>
                  <SelectContent>
                    {counselors.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!editLead && (
              <div className="md:col-span-2">
                <Label>Initial Remark (optional)</Label>
                <Textarea rows={2} value={form.remarks_text} onChange={(e) => setForm({ ...form, remarks_text: e.target.value })} data-testid="lead-initial-remark" />
              </div>
            )}
            <div className="md:col-span-2">
              <Label>Admission Status</Label>
              <Select value={form.admission_status} onValueChange={(v) => setForm({ ...form, admission_status: v })}>
                <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
              {!isAdmin() && !canManageAll && !isTeamLead && editLead && form.admission_status === 'Confirmed' && editLead.admission_status !== 'Confirmed' && (
                <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Clicking Save will open the Add Admission form with pre-filled details.
                </p>
              )}
            </div>
          </div>

          {editLead && (
            <div className="mt-4 border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <Activity className="h-4 w-4" /> Activity & Remarks
              </h4>
              <ActivityTimeline lead={editLead} onAddRemark={addRemark} />
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-lead-btn" className="bg-[#0066CC] hover:bg-[#0052A3]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editLead ? 'Save changes' : 'Create Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admission dialog — opens when Counselor marks a lead Confirmed */}
      <Dialog open={admissionDialogOpen} onOpenChange={(o) => {
        if (!o) { setAdmissionDialogOpen(false); setPendingConfirmLead(null); setAdmissionForm(null); }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" /> Add Admission
            </DialogTitle>
            <DialogDescription>
              Confirming this lead will create an Admission record and mark it as Confirmed. Details are auto-filled from the lead — feel free to edit.
            </DialogDescription>
          </DialogHeader>

          {admissionForm && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div>
                <Label>Candidate Name<span className="text-rose-500">*</span></Label>
                <Input
                  value={admissionForm.candidate_name}
                  onChange={(e) => setAdmissionForm({ ...admissionForm, candidate_name: e.target.value })}
                  data-testid="adm-candidate-name"
                />
              </div>
              <div>
                <Label>Place<span className="text-rose-500">*</span></Label>
                <Input
                  value={admissionForm.place}
                  onChange={(e) => setAdmissionForm({ ...admissionForm, place: e.target.value })}
                  data-testid="adm-place"
                />
              </div>
              <div>
                <Label>College<span className="text-rose-500">*</span></Label>
                <Select
                  value={admissionForm.college_id}
                  onValueChange={(v) => {
                    const c = colleges.find((x) => x.id === v);
                    setAdmissionForm({ ...admissionForm, college_id: v, college_name: c?.name || '', course_id: '', course_name: '' });
                  }}
                >
                  <SelectTrigger data-testid="adm-college"><SelectValue placeholder="Select college" /></SelectTrigger>
                  <SelectContent>
                    {(pendingConfirmLead?.suggested_college_ids || []).map((cid) => {
                      const c = colleges.find((x) => x.id === cid);
                      const name = c?.name || (pendingConfirmLead.suggested_college_names || [])[(pendingConfirmLead.suggested_college_ids || []).indexOf(cid)] || cid;
                      return <SelectItem key={cid} value={cid}>{name}</SelectItem>;
                    })}
                    {/* Fallback: if the lead has no suggested colleges, allow picking from the full list */}
                    {(!pendingConfirmLead?.suggested_college_ids || pendingConfirmLead.suggested_college_ids.length === 0) && colleges.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Course<span className="text-rose-500">*</span></Label>
                <Select
                  value={admissionForm.course_id}
                  onValueChange={(v) => {
                    const c = admissionCourseOptions.find((x) => x.id === v);
                    setAdmissionForm({ ...admissionForm, course_id: v, course_name: c?.name || '' });
                  }}
                >
                  <SelectTrigger data-testid="adm-course"><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    {admissionCourseOptions.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                    {admissionCourseOptions.length === 0 && (
                      <SelectItem value="__none__" disabled>No courses loaded — select college first</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Admission Date<span className="text-rose-500">*</span></Label>
                <Input
                  type="date"
                  value={admissionForm.admission_date}
                  onChange={(e) => setAdmissionForm({ ...admissionForm, admission_date: e.target.value })}
                  data-testid="adm-date"
                />
              </div>
              <div>
                <Label>Total Fees (₹)<span className="text-rose-500">*</span></Label>
                <Input
                  type="number" min="0" step="100"
                  value={admissionForm.total_fees}
                  onChange={(e) => setAdmissionForm({ ...admissionForm, total_fees: e.target.value })}
                  data-testid="adm-total-fees"
                />
              </div>
              <div>
                <Label>Fees Paid (₹)</Label>
                <Input
                  type="number" min="0" step="100"
                  value={admissionForm.fees_paid}
                  onChange={(e) => setAdmissionForm({ ...admissionForm, fees_paid: e.target.value })}
                  data-testid="adm-fees-paid"
                />
              </div>
              <div>
                <Label>Scholarship (₹)</Label>
                <Input
                  type="number" min="0" step="100"
                  placeholder="Leave blank if none"
                  value={admissionForm.scholarship_amount}
                  onChange={(e) => setAdmissionForm({ ...admissionForm, scholarship_amount: e.target.value })}
                  data-testid="adm-scholarship"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Remark</Label>
                <Textarea
                  rows={2}
                  value={admissionForm.remark}
                  onChange={(e) => setAdmissionForm({ ...admissionForm, remark: e.target.value })}
                  data-testid="adm-remark"
                />
              </div>
              {Number(admissionForm.total_fees || 0) > 0 && (
                <div className="md:col-span-2 bg-slate-50 rounded-md p-3 text-sm flex items-center justify-between">
                  <span className="text-slate-600">Balance</span>
                  <span className="font-semibold text-[#0066CC]">
                    {inr(Math.max(Number(admissionForm.total_fees || 0) - Number(admissionForm.fees_paid || 0), 0))}
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAdmissionDialogOpen(false); setPendingConfirmLead(null); setAdmissionForm(null); }}>
              Cancel
            </Button>
            <Button
              onClick={saveAdmission}
              disabled={savingAdmission}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-admission-btn"
            >
              {savingAdmission ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Confirm & Save Admission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LeadFeesShareDialog
        lead={waFeesLead}
        open={!!waFeesLead}
        onOpenChange={(o) => { if (!o) setWaFeesLead(null); }}
      />
    </div>
  );
}
